import {Button, Flag} from 'platform/components';
import {VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  type AdvertisementPreflightIssue,
  type AdvertisementPreflightStatus,
  useGetVehicleQuery,
} from '@dms/api/sales';
import {type AdvertisingPlatformCodeEnum, EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {usePermissions, useVehiclePhotosPermissions} from '@dms/shared';

import {useOpenPlatformDetail} from '../hooks/useOpenPlatformDetail';
import {openPlatformPreflightDialog} from '../utils/openPlatformPreflightDialog';

type PlatformPreflightStatusProps = {
  vehicleId: string;
  platformCode: AdvertisingPlatformCodeEnum;
  platformTitle: string;
  status?: AdvertisementPreflightStatus;
  issues: AdvertisementPreflightIssue[];
  isLoading: boolean;
  isError: boolean;
};

export function PlatformPreflightStatus(props: PlatformPreflightStatusProps) {
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});
  const {data: participation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });
  const [canEditVehicle, canEditPlatform] = usePermissions({
    permissionKeys: ['updateVehicle', 'editPlatformSettings'],
    scopes: {
      updateVehicle: {participation, branchId: vehicle?.branchId},
      editPlatformSettings: {participation, branchId: vehicle?.branchId},
    },
  });
  const {canReadVehicleAlbumPhotos} = useVehiclePhotosPermissions({vehicleId: props.vehicleId});
  const openPlatformDetail = useOpenPlatformDetail({
    vehicleId: props.vehicleId,
    isDisabled: !canEditPlatform,
  });
  const handleOpenPlatformDetail = () =>
    openPlatformDetail(props.platformCode, props.platformTitle);

  return (
    match({isLoading: props.isLoading, isError: props.isError, status: props.status})
      .with({isLoading: true}, () => (
        <Flag colorScheme="blue" label={i18n.t('entity.vehicle.labels.availabilityChecking')} />
      ))
      .with({isError: true}, () => (
        <Flag colorScheme="neutral" label={i18n.t('entity.workshop.labels.notAvailable')} />
      ))
      // BE contract: 'ready' is the only non-blocking preflight status - fail closed on anything else.
      .with({status: Pattern.string.and(Pattern.not('ready'))}, () => (
        <VStack spacing={1} align="flex-start">
          <Flag colorScheme="red" label={i18n.t('general.labels.blocked')} />
          <Button
            variant="link"
            size="small"
            title={i18n.t('general.labels.details')}
            onClick={() =>
              openPlatformPreflightDialog({
                ...props,
                canEditVehicle,
                canEditPlatform,
                canReadPhotos: canReadVehicleAlbumPhotos,
                onOpenPlatformDetail: handleOpenPlatformDetail,
              })
            }
          />
        </VStack>
      ))
      .otherwise(() => null)
  );
}
